'use client';

import '../../context-extractors/InspectViewContextExtractors';
import './blog-immersive.css';
import './reading-themes.css';
import '../../../../ux/features/website-assembler/atmosphere/site-atmosphere.css';
import '../../../../ux/features/website-assembler/vision/vision-cta.css';
import '../../../../ux/components/editors/BolderEditor/styles/editor.css';

import { generateHTML } from '@tiptap/html';
import StarterKit from '@tiptap/starter-kit';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import {
  ArrowRight,
  Bookmark,
  Calendar,
  CheckCircle,
  ChevronDown,
  Crown,
  ExternalLink,
  Heart,
  Key,
  Mail,
  Share2,
  Sparkles,
  User,
} from 'lucide-react';
import Image from 'next/image';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  useAmbientIntelligenceActions,
  useAmbientIntelligenceState,
} from '@/core/ambient-intelligence/hooks/useAmbientIntelligence';
import { updateAppSession } from '@/core/apis/app-api';
import { getBoardDataById } from '@/core/apis/bentoboard-apis';
import { signInMagicLink, validateOTP } from '@/core/queries/auth-queries';
import { appendImageRepoDomainIfNotExistInUrl } from '@/core/utils/helpers';
import { appSettings } from '@/settings/app-settings';
import { ContentViewer } from '@/ux/components/editors/BolderEditor/components/ContentViewer';
import { ContentViewerViewport } from '@/ux/components/editors/BolderEditor/components/ContentViewerViewport';
import { ContentGrid } from '@/ux/components/editors/BolderEditor/extensions/ContentGrid';
import { CustomVideo } from '@/ux/components/editors/BolderEditor/extensions/CustomVideo';
import { cn } from '@/ux/utils';
import Lightbox from '@/ux-legacy/components/common/Lightbox/Lightbox';

import { ReadingThemeSwitcher } from './components/ReadingThemeSwitcher';
import { calculateReadingTime, extractTextFromHtml } from './utils/contentEnhancer';
import { readingThemes } from './utils/readingThemes';

// Helper to convert TipTap JSON to HTML
const convertTipTapToHtml = (content: any): string => {
  try {
    // If it's already a string, check if it's JSON
    if (typeof content === 'string') {
      try {
        const parsed = JSON.parse(content);
        if (parsed && typeof parsed === 'object' && parsed.type === 'doc') {
          content = parsed;
        } else {
          // It's not TipTap JSON, return as is
          return content;
        }
      } catch {
        // Not JSON, return as is
        return content;
      }
    }

    // If it's TipTap JSON object, convert to HTML
    if (typeof content === 'object' && content.type === 'doc') {
      const extensions = [
        StarterKit.configure({
          heading: {
            levels: [1, 2, 3],
          },
          codeBlock: false,
        }),
        CustomVideo.configure({
          inline: false,
          HTMLAttributes: {},
        }),
        ContentGrid,
      ];

      return generateHTML(content, extensions);
    }

    return content || '';
  } catch (error) {
    console.error('Error converting TipTap to HTML:', error);
    return content || '';
  }
};

interface BlogInspectImmersiveProps {
  content: any; // Accept any content format
  isUserLoggedIn?: boolean;
  userEmail?: string;
  onClose?: () => void;
  onAction?: (action: string, content: any, data?: any) => void;
}

export const BlogInspectImmersive: React.FC<BlogInspectImmersiveProps> = ({
  content,
  isUserLoggedIn = false,
  userEmail = '',
  onClose,
  onAction,
}) => {
  const { pushData } = useAmbientIntelligenceActions();
  const { genericContext } = useAmbientIntelligenceState();

  // Extract data from ContentItem format or direct board data
  const [blogData, setBlogData] = useState(() => {
    // Handle ContentItem format from BlogListWithInspect
    if (content.data) {
      return {
        id: content.id,
        title: content.title,
        description: content.description,
        displayImage: content.media?.hero?.url
          ? appendImageRepoDomainIfNotExistInUrl(content.media.hero.url)
          : undefined,
        vendor: content.source?.author
          ? {
              Name: content.source.author.name,
              DisplayPic: content.source.author.avatar
                ? appendImageRepoDomainIfNotExistInUrl(content.source.author.avatar)
                : undefined,
              Headline: content.source.author.role,
            }
          : undefined,
        createdAt: content.temporal?.publishedAt || content.temporal?.createdAt,
        elements: content.data.elements,
        theme: content.data.theme,
        accessType: content.data.accessType,
        isMember: content.data.isMember,
        generatedSummary: content.data.generatedSummary || content.data.excerpt,
        questions: content.data.questions,
        htmlContent: content.data.htmlContent,
      };
    }

    // Handle direct board API response
    if (content.vendor) {
      const rawText = content.elements?.[0]?.content?.content?.text;
      return {
        id: content._id,
        title: content.name,
        description: content.description,
        displayImage: content.displayImage
          ? appendImageRepoDomainIfNotExistInUrl(content.displayImage)
          : undefined,
        vendor: content.vendor
          ? {
              Name: content.vendor.businessName,
              DisplayPic: content.vendor.businessProfileImage
                ? appendImageRepoDomainIfNotExistInUrl(content.vendor.businessProfileImage)
                : undefined,
              Headline: content.vendor.businessDescription?.split('\n')[0], // First line of description
            }
          : undefined,
        createdAt: content.createdAt,
        elements: content.elements,
        theme: content.theme,
        accessType: content.accessType,
        isMember: content.isMember,
        generatedSummary: content.generatedSummary,
        questions: content.generatedSummary?.questions || [],
        htmlContent: rawText,
      };
    }

    // Fallback to direct content
    return content;
  });

  // Hide scrollbar globally for immersive experience
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      html {
        scrollbar-width: none !important;
        -ms-overflow-style: none !important;
      }
      html::-webkit-scrollbar {
        display: none !important;
      }
      body {
        scrollbar-width: none !important;
        -ms-overflow-style: none !important;
      }
      body::-webkit-scrollbar {
        display: none !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Apply theme styles
  useEffect(() => {
    if (blogData.theme) {
      const root = document.documentElement;
      const theme = blogData.theme;

      // Handle API theme format with sourcePaletteColors
      if (theme.sourcePaletteColors && theme.sourcePaletteColors.length >= 4) {
        const [bg, light, dark, primary] = theme.sourcePaletteColors;
        root.style.setProperty('--blog-primary-color', primary);
        root.style.setProperty('--blog-secondary-color', dark);
        root.style.setProperty('--blog-bg-color', bg);
        root.style.setProperty('--blog-text-color', dark);
      } else {
        // Handle ContentItem format
        if (theme.primaryColor) {
          root.style.setProperty('--blog-primary-color', theme.primaryColor);
        }
        if (theme.secondaryColor) {
          root.style.setProperty('--blog-secondary-color', theme.secondaryColor);
        }
        if (theme.backgroundColor) {
          root.style.setProperty('--blog-bg-color', theme.backgroundColor);
        }
        if (theme.textColor) {
          root.style.setProperty('--blog-text-color', theme.textColor);
        }
      }

      return () => {
        // Clean up on unmount
        root.style.removeProperty('--blog-primary-color');
        root.style.removeProperty('--blog-secondary-color');
        root.style.removeProperty('--blog-bg-color');
        root.style.removeProperty('--blog-text-color');
      };
    }
  }, [blogData.theme]);

  // Trigger getSuggestions when component mounts and genericContext is available
  useEffect(() => {
    // Only call pushData once when component mounts and we have cognitionId
    if (blogData.id && genericContext?.cognitionId) {
      pushData({
        pageType: 'blog',
        contentType: 'blog',
        contentId: blogData.id,
      });
    }
  }, [genericContext?.cognitionId]); // Re-run when cognitionId becomes available

  // State
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [showActions, setShowActions] = useState(true);
  const [readingTime, setReadingTime] = useState(0);
  const [activeSection, setActiveSection] = useState('');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<{ url: string; alt?: string } | null>(null);
  const [videoStates, setVideoStates] = useState<Record<string, { playing: boolean; muted: boolean }>>({});
  const [accessCode, setAccessCode] = useState('');
  const [isCodeValid, setIsCodeValid] = useState(false);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [currentReadingTheme, setCurrentReadingTheme] = useState<string | null>(null);

  // Authentication state
  const [authStage, setAuthStage] = useState<'initial' | 'email-input' | 'otp-input' | 'success'>('initial');
  const [email, setEmail] = useState(isUserLoggedIn ? userEmail : '');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [sessionToken, setSessionToken] = useState('');
  const [authError, setAuthError] = useState('');
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [hasEmailAccess, setHasEmailAccess] = useState(false);
  const otpRefs = useRef<HTMLInputElement[]>([]);

  // Update email when userEmail prop changes
  useEffect(() => {
    if (userEmail && userEmail !== email && isUserLoggedIn) {
      setEmail(userEmail);
    }
  }, [userEmail]);

  // Refs
  const contentRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const sectionsRef = useRef<Record<string, HTMLElement>>({});

  // Scroll animations
  const scrollYProgress = useMotionValue(0);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  const heroOpacity = useTransform(scrollYProgress, [0, 1], [1, 0]);
  const heroBlur = useTransform(scrollYProgress, [0, 1], [0, 5]);

  // Access control
  const getAccessState = useCallback(() => {
    if (!blogData.accessType || blogData.accessType === 'PUBLIC' || blogData.accessType === 'NONE')
      return 'open';
    if (blogData.accessType === 'MEMBER_EXCLUSIVE') return 'member-exclusive';
    if (blogData.accessType === 'CODE' || blogData.accessType === 'PASSWORD')
      return isCodeValid ? 'code-authenticated' : 'code-required';
    if (blogData.accessType === 'EMAIL') {
      return (isUserLoggedIn && blogData.isMember) || hasEmailAccess
        ? 'email-authenticated'
        : 'email-required';
    }
    return 'open';
  }, [blogData.accessType, blogData.isMember, isUserLoggedIn, isCodeValid, hasEmailAccess]);

  const accessState = getAccessState();
  const hasFullAccess = ['open', 'email-authenticated', 'code-authenticated'].includes(accessState);

  // Get content for ContentViewer (JSON format)
  const contentData = useMemo(() => {
    if (!blogData) return null;

    if (blogData.elements && blogData.elements.length > 0) {
      // Find the first text element
      const textElement = blogData.elements.find(
        (el) =>
          (el.contentType === 'custom' || el.contentType === 'text' || el.contentType === 'TEXT') &&
          el.content?.content?.text,
      );
      if (textElement?.content?.content?.text) {
        const content = textElement.content.content.text;
        // If it's already JSON, return it; otherwise parse it
        if (typeof content === 'string') {
          try {
            return JSON.parse(content);
          } catch {
            // If parsing fails, it might be HTML - convert it using generateHTML reverse
            // For now, return null and use the HTML content fallback
            return null;
          }
        }
        return content;
      }
    }
    // Fallback to htmlContent if available
    if (blogData.htmlContent) {
      const content = blogData.htmlContent;
      if (typeof content === 'string') {
        try {
          return JSON.parse(content);
        } catch {
          // If it's not JSON, return null
          return null;
        }
      }
      return content;
    }
    return null;
  }, [blogData]);

  // Keep HTML content as fallback for non-JSON content
  const htmlContent = useMemo(() => {
    if (contentData) return ''; // If we have JSON, don't need HTML

    if (blogData.elements && blogData.elements.length > 0) {
      const textElement = blogData.elements.find(
        (el) =>
          (el.contentType === 'custom' || el.contentType === 'text' || el.contentType === 'TEXT') &&
          el.content?.content?.text,
      );
      if (textElement?.content?.content?.text) {
        return convertTipTapToHtml(textElement.content.content.text);
      }
    }
    if (blogData.htmlContent) {
      return convertTipTapToHtml(blogData.htmlContent);
    }
    return '';
  }, [blogData, contentData]);

  // Calculate reading time
  useEffect(() => {
    if (blogData.elements && blogData.elements.length > 0) {
      const totalText = blogData.elements
        .filter(
          (el) =>
            (el.contentType === 'custom' || el.contentType === 'text' || el.contentType === 'TEXT') &&
            el.content?.content?.text,
        )
        .map((el) => extractTextFromHtml(el.content.content.text || ''))
        .join(' ');

      if (totalText) {
        setReadingTime(calculateReadingTime(totalText));
      } else {
        setReadingTime(5); // Default fallback
      }
    } else if (blogData.htmlContent) {
      // Use htmlContent if elements not available
      const totalText = extractTextFromHtml(blogData.htmlContent);
      setReadingTime(calculateReadingTime(totalText));
    } else {
      setReadingTime(5); // Default fallback
    }
  }, [blogData.elements, blogData.htmlContent]);

  // Handle button click events
  useEffect(() => {
    const handleButtonClick = (e: Event) => {
      const target = e.target as HTMLElement;
      const button = target.closest('.immersive-button');

      if (button) {
        const href = button.getAttribute('href');
        const buttonType = button.getAttribute('data-button-type');

        if (buttonType === 'product' && href?.includes('productId=')) {
          e.preventDefault();
          const productIdMatch = href.match(/productId=([^&]+)/);
          const productId = productIdMatch?.[1];

          if (productId && onAction) {
            onAction('openProduct', blogData, { productId, href });
          }
        } else if (buttonType === 'story' && href?.includes('/view?id=')) {
          e.preventDefault();
          const contentIdMatch = href.match(/id=([^&]+)/);
          const contentId = contentIdMatch?.[1];

          if (contentId && onAction) {
            onAction('openStory', blogData, { contentId, href });
          }
        }
      }
    };

    // Add event listener for button clicks
    document.addEventListener('click', handleButtonClick);

    return () => {
      document.removeEventListener('click', handleButtonClick);
    };
  }, [blogData, onAction]);

  // Handle scroll with throttling for 60fps
  useEffect(() => {
    let ticking = false;

    const handleScroll = (event?: Event) => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          // Find the scrollable container (the modal content area)
          const scrollContainer =
            (event?.target as HTMLElement) ||
            (document.querySelector('.overflow-y-auto') as HTMLElement) ||
            window;

          // Get scroll values based on container type
          const scrollTop = scrollContainer === window ? window.scrollY : scrollContainer.scrollTop;

          const scrollHeight =
            scrollContainer === window ? document.documentElement.scrollHeight : scrollContainer.scrollHeight;

          const clientHeight = scrollContainer === window ? window.innerHeight : scrollContainer.clientHeight;

          const heroHeight = heroRef.current?.offsetHeight || 500;

          // Update scroll progress for hero animations
          const heroProgress = Math.min(scrollTop / heroHeight, 1);
          scrollYProgress.set(heroProgress);

          // Update overall reading progress
          const maxScroll = scrollHeight - clientHeight;
          const progress = maxScroll > 0 ? (scrollTop / maxScroll) * 100 : 0;
          setScrollProgress(Math.min(progress, 100)); // Ensure it doesn't exceed 100%

          // Hide/show actions based on scroll
          setShowActions(scrollTop < 200);

          // Update active section
          Object.entries(sectionsRef.current).forEach(([id, element]) => {
            if (element) {
              const rect = element.getBoundingClientRect();
              if (rect.top >= 0 && rect.top <= clientHeight / 2) {
                setActiveSection(id);
              }
            }
          });

          ticking = false;
        });
        ticking = true;
      }
    };

    // Try to find the scrollable container
    const findScrollContainer = () => {
      // Look for the modal's scrollable content area
      // First try the most specific selector, then fallback to more general ones
      const modalContent =
        document.querySelector('.content-viewer-wrapper') ||
        document.querySelector('.flex-1.relative.overflow-y-auto') ||
        document.querySelector('.overflow-y-auto') ||
        document.querySelector('[style*="overflow-y: auto"]');
      if (modalContent) {
        modalContent.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll({ target: modalContent } as any); // Initial call with container
        return () => modalContent.removeEventListener('scroll', handleScroll);
      } else {
        // Fallback to window scroll
        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll(); // Initial call
        return () => window.removeEventListener('scroll', handleScroll);
      }
    };

    // Wait a bit for the modal to be fully rendered
    const cleanup = setTimeout(() => {
      const removeListener = findScrollContainer();
      // Store the cleanup function
      if (removeListener) {
        (window as any).__blogScrollCleanup = removeListener;
      }
    }, 100);

    return () => {
      clearTimeout(cleanup);
      if ((window as any).__blogScrollCleanup) {
        (window as any).__blogScrollCleanup();
        delete (window as any).__blogScrollCleanup;
      }
    };
  }, []);

  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Recently';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Handle image click
  const handleImageClick = (url: string, alt?: string) => {
    setLightboxImage({ url, alt });
    setLightboxOpen(true);
  };

  // Handle video controls
  const toggleVideo = (videoId: string) => {
    setVideoStates((prev) => ({
      ...prev,
      [videoId]: {
        ...prev[videoId],
        playing: !prev[videoId]?.playing,
      },
    }));
  };

  const toggleVideoMute = (videoId: string) => {
    setVideoStates((prev) => ({
      ...prev,
      [videoId]: {
        ...prev[videoId],
        muted: !prev[videoId]?.muted,
      },
    }));
  };

  // Handle code/password submission
  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (accessCode.length === 0) {
      setAuthError('Please enter a password');
      return;
    }

    setIsAuthLoading(true);
    setAuthError('');

    try {
      if (blogData.accessType === 'PASSWORD') {
        // Call API with password
        const response = await getBoardDataById(appSettings.api, {
          boardId: content?.id || blogData.id,
          // accessEmail: true,
          password: accessCode,
        });
        // Check if password is invalid (statusCode 423)
        if (response?.statusCode === 423) {
          setAuthError(response.message || 'Invalid password. Please try again.');
          setAccessCode(''); // Clear the password field
          return;
        }

        if (response && response.elements) {
          // Update blogData with the API response - password was correct
          setBlogData({
            id: response._id || blogData.id,
            title: response.name || blogData.title,
            description: response.description || blogData.description,
            displayImage: response.displayImage
              ? appendImageRepoDomainIfNotExistInUrl(response.displayImage)
              : blogData.displayImage,
            vendor: response.vendor
              ? {
                  Name: response.vendor.businessName || response.vendor.Name,
                  DisplayPic: response.vendor.businessProfileImage
                    ? appendImageRepoDomainIfNotExistInUrl(response.vendor.businessProfileImage)
                    : response.vendor.DisplayPic,
                  Headline: response.vendor.businessDescription?.split('\n')[0] || response.vendor.Headline,
                }
              : blogData.vendor,
            createdAt: response.createdAt || blogData.createdAt,
            elements: response.elements || blogData.elements,
            theme: response.theme || blogData.theme,
            accessType: response.accessType || blogData.accessType,
            isMember: true, // User now has member access
            generatedSummary: response.generatedSummary || blogData.generatedSummary,
            questions: response.generatedSummary?.questions || blogData.questions || [],
            htmlContent: response.elements?.[0]?.content?.content?.text || blogData.htmlContent,
          });

          // Access granted
          setIsCodeValid(true);
          // onAction?.('access_granted', blogData, { password: accessCode });
        } else {
          setAuthError('Failed to authenticate. Please try again.');
        }
      } else {
        // Original CODE logic
        setIsCodeValid(true);
        onAction?.('access_granted', blogData, { code: accessCode });
      }
    } catch (error: any) {
      setAuthError(error?.response?.data?.message || 'Invalid password. Please try again.');
    } finally {
      setIsAuthLoading(false);
    }
  };

  // Handle product clicks
  useEffect(() => {
    const handleProductClick = (event: CustomEvent) => {
      const { productId, href } = event.detail;
      onAction?.('product_click', blogData, { productId, href });
    };

    window.addEventListener('productClick' as any, handleProductClick);
    return () => {
      window.removeEventListener('productClick' as any, handleProductClick);
    };
  }, [blogData, onAction]);

  // Authentication handlers
  const handleContinueWithEmail = async () => {
    // Check if user already has access (shouldn't happen but just in case)
    if (hasEmailAccess || (isUserLoggedIn && blogData.isMember)) {
      return;
    }

    if (isUserLoggedIn && userEmail) {
      // If user is logged in with email, directly proceed to email input with pre-filled email
      setEmail(userEmail);
      setAuthStage('email-input');
      // Auto-submit can be enabled if desired
      // setTimeout(() => handleEmailSubmit(), 100);
    } else {
      // Show email input form
      console.log('userEmail', userEmail, 'email', email);

      setAuthStage('email-input');
    }
  };

  const handleEmailSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!email) return;

    setIsAuthLoading(true);
    setAuthError('');

    try {
      if (isUserLoggedIn && userEmail) {
        // User is logged in, call API to grant access
        const response = await getBoardDataById(appSettings.api, {
          boardId: content?.id || blogData.id,
          accessEmail: true,
        });

        if (response) {
          // Update blogData with the API response
          setBlogData({
            id: response._id || blogData.id,
            title: response.name || blogData.title,
            description: response.description || blogData.description,
            displayImage: response.displayImage
              ? appendImageRepoDomainIfNotExistInUrl(response.displayImage)
              : blogData.displayImage,
            vendor: response.vendor
              ? {
                  Name: response.vendor.businessName || response.vendor.Name,
                  DisplayPic: response.vendor.businessProfileImage
                    ? appendImageRepoDomainIfNotExistInUrl(response.vendor.businessProfileImage)
                    : response.vendor.DisplayPic,
                  Headline: response.vendor.businessDescription?.split('\n')[0] || response.vendor.Headline,
                }
              : blogData.vendor,
            createdAt: response.createdAt || blogData.createdAt,
            elements: response.elements || blogData.elements,
            theme: response.theme || blogData.theme,
            accessType: response.accessType || blogData.accessType,
            isMember: true, // User now has member access
            generatedSummary: response.generatedSummary || blogData.generatedSummary,
            questions: response.generatedSummary?.questions || blogData.questions || [],
            htmlContent: response.elements?.[0]?.content?.content?.text || blogData.htmlContent,
          });

          // Access granted - update state to show content
          setHasEmailAccess(true);
          setAuthStage('success');

          // Show success briefly then display content
          setTimeout(() => {
            // The access state will automatically update due to hasEmailAccess being true
          }, 1500);
        } else {
          setAuthError('Failed to grant access. Please try again.');
        }
      } else {
        // User not logged in, send magic link
        const result = await signInMagicLink(appSettings.api.GRAPHQL, email);

        if (result?.data?.signInMagicLink?.session) {
          setSessionToken(result?.data?.signInMagicLink?.session);
          setAuthStage('otp-input');
          setOtp(['', '', '', '', '', '']);
          setTimeout(() => otpRefs.current[0]?.focus(), 100);
        } else {
          setAuthError('Failed to send verification code');
          setAuthStage('email-input');
        }
        // if (response?.SessionToken) {
        //   setSessionToken(response.SessionToken);
        //   setAuthStage('otp-input');
        // } else {
        //   setAuthError('Failed to send verification code. Please try again.');
        // }
      }
    } catch (err: any) {
      setAuthError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsAuthLoading(false);
    }
  };

  const handleOTPChange = (index: number, value: string) => {
    if (value.length > 1) {
      // Handle paste
      const pastedValue = value.slice(0, 6);
      const newOtp = [...otp];
      for (let i = 0; i < pastedValue.length && index + i < 6; i++) {
        newOtp[index + i] = pastedValue[i];
      }
      setOtp(newOtp);

      // Focus last filled input or next empty one
      const lastFilledIndex = Math.min(index + pastedValue.length - 1, 5);
      otpRefs.current[lastFilledIndex]?.focus();

      // Auto-submit if all filled
      if (newOtp.every((digit) => digit)) {
        handleOTPSubmit(newOtp.join(''));
      }
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }

    // Auto-submit when complete
    if (newOtp.every((digit) => digit)) {
      handleOTPSubmit(newOtp.join(''));
    }
  };

  const handleOTPSubmit = async (otpValue?: string) => {
    const code = otpValue || otp.join('');
    if (code.length !== 6) return;

    setIsAuthLoading(true);
    setAuthError('');

    try {
      const result = await validateOTP(appSettings.api.GRAPHQL, code, email, sessionToken);
      if (result?.data?.respondToChallenge) {
        // Update session
        await updateAppSession({
          jwt: result?.data?.respondToChallenge?.jwt,
          refreshToken: result?.data?.respondToChallenge?.refreshToken,
        });

        // Fetch updated board data with access
        const response = await getBoardDataById(appSettings.api, {
          boardId: content?.id || blogData.id,
          accessEmail: true,
        });

        if (response) {
          // Update blogData with the API response
          setBlogData({
            id: response._id || blogData.id,
            title: response.name || blogData.title,
            description: response.description || blogData.description,
            displayImage: response.displayImage
              ? appendImageRepoDomainIfNotExistInUrl(response.displayImage)
              : blogData.displayImage,
            vendor: response.vendor
              ? {
                  Name: response.vendor.businessName || response.vendor.Name,
                  DisplayPic: response.vendor.businessProfileImage
                    ? appendImageRepoDomainIfNotExistInUrl(response.vendor.businessProfileImage)
                    : response.vendor.DisplayPic,
                  Headline: response.vendor.businessDescription?.split('\n')[0] || response.vendor.Headline,
                }
              : blogData.vendor,
            createdAt: response.createdAt || blogData.createdAt,
            elements: response.elements || blogData.elements,
            theme: response.theme || blogData.theme,
            accessType: response.accessType || blogData.accessType,
            isMember: true, // User now has member access
            generatedSummary: response.generatedSummary || blogData.generatedSummary,
            questions: response.generatedSummary?.questions || blogData.questions || [],
            htmlContent: response.elements?.[0]?.content?.content?.text || blogData.htmlContent,
          });

          // Access granted - update state to show content
          setHasEmailAccess(true);
          setAuthStage('success');

          // Show success briefly then display content
          setTimeout(() => {
            // The access state will automatically update due to hasEmailAccess being true
          }, 1500);
        } else {
          setAuthError('Failed to grant access. Please try again.');
        }

        // if (response?.isValid) {
        //   // Update app session
        //   await updateAppSession(null, {
        //     jwtToken: response.AuthenticationResult.IdToken,
        //     refreshToken: response.AuthenticationResult.RefreshToken,
        //     accessToken: response.AuthenticationResult.AccessToken,
        //     profileData: response.profileData,
        //   });

        //   setAuthStage('success');
        //   // Reload the page or update state to show content
        //   setTimeout(() => {
        //     window.location.reload();
        //   }, 1500);
      } else {
        setAuthError('Invalid verification code. Please try again.');
        setOtp(['', '', '', '', '', '']);
        otpRefs.current[0]?.focus();
      }
    } catch (err: any) {
      setAuthError(err.message || 'Invalid code. Please try again.');
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } finally {
      setIsAuthLoading(false);
    }
  };

  // Render access gate
  const renderAccessGate = () => {
    switch (accessState) {
      case 'email-required':
        // Always show the header and description
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center p-12 text-center"
          >
            <div className="w-24 h-24 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center mb-8 shadow-2xl">
              <Mail className="w-12 h-12 text-white" />
            </div>
            <h3 className="text-3xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Premium Content Awaits
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md text-lg">
              Sign in to unlock this exclusive story and join our community of curious readers.
            </p>

            {/* Conditionally show button, email input, or OTP input */}
            <div className="w-full max-w-sm">
              {authStage === 'initial' ? (
                <button
                  onClick={handleContinueWithEmail}
                  className="w-full px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full hover:shadow-xl transition-all transform hover:-translate-y-1 flex items-center justify-center gap-3 text-lg font-medium cursor-pointer"
                >
                  <Mail className="w-5 h-5" />
                  Continue with Email
                </button>
              ) : authStage === 'email-input' ? (
                <motion.form
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.2 }}
                  onSubmit={handleEmailSubmit}
                  className="w-full"
                >
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-600 dark:text-blue-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="w-full pl-12 pr-14 py-4 text-base border-2 border-blue-200 dark:border-blue-800/30 rounded-full bg-white/90 dark:bg-gray-800/90 transition-all focus:outline-none focus:border-blue-500 focus:bg-white dark:focus:bg-gray-800 focus:shadow-[0_0_0_4px_rgba(59,130,246,0.1)]"
                      required
                    />
                    <button
                      type="submit"
                      disabled={!email || isAuthLoading}
                      className="absolute right-1 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-full transition-all hover:scale-105 disabled:hover:scale-100"
                    >
                      {isAuthLoading ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <ArrowRight className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  {authError && (
                    <p className="text-red-500 text-sm mt-2 text-center animate-fadeIn">{authError}</p>
                  )}
                </motion.form>
              ) : authStage === 'otp-input' ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.2 }}
                  className="w-full"
                >
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    We sent a verification code to <strong>{email}</strong>
                  </p>

                  <div className="flex gap-2 justify-center mb-4">
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => (otpRefs.current[index] = el!)}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOTPChange(index, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Backspace' && !digit && index > 0) {
                            otpRefs.current[index - 1]?.focus();
                          }
                        }}
                        className={cn(
                          'w-12 h-12 text-xl font-semibold text-center border-2 rounded-xl transition-all',
                          'bg-white/90 dark:bg-gray-800/90',
                          'border-blue-200 dark:border-blue-800/30',
                          'focus:outline-none focus:border-blue-500 focus:bg-white dark:focus:bg-gray-800',
                          'focus:transform focus:scale-105 focus:shadow-[0_0_0_4px_rgba(59,130,246,0.1)]',
                          digit && 'border-blue-500 bg-blue-50/50 dark:bg-blue-900/20',
                        )}
                        aria-label={`Digit ${index + 1}`}
                      />
                    ))}
                  </div>

                  {isAuthLoading && (
                    <div className="text-center mb-4">
                      <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                      <p className="text-sm text-gray-600 dark:text-gray-400">Verifying code...</p>
                    </div>
                  )}

                  {authError && (
                    <p className="text-red-500 text-sm mb-4 text-center animate-fadeIn">{authError}</p>
                  )}

                  <button
                    onClick={() => {
                      setAuthStage('email-input');
                      setOtp(['', '', '', '', '', '']);
                      setAuthError('');
                    }}
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline w-full text-center"
                  >
                    Use a different email
                  </button>
                </motion.div>
              ) : authStage === 'success' ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                    className="w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-xl"
                  >
                    <CheckCircle className="w-10 h-10 text-white" />
                  </motion.div>
                  <p className="text-lg font-medium text-gray-900 dark:text-white">Access Granted!</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Opening content...</p>
                </motion.div>
              ) : null}
            </div>
          </motion.div>
        );

      case 'code-required':
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center p-12 text-center"
          >
            <div className="w-24 h-24 bg-gradient-to-br from-orange-400 to-pink-600 rounded-full flex items-center justify-center mb-8 shadow-2xl">
              <Key className="w-12 h-12 text-white" />
            </div>
            <h3 className="text-3xl font-bold mb-4 bg-gradient-to-r from-orange-600 to-pink-600 bg-clip-text text-transparent">
              {blogData.accessType === 'PASSWORD' ? 'Password Protected' : 'Secret Garden'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md text-lg">
              {blogData.accessType === 'PASSWORD'
                ? 'This content is password protected. Enter the password to continue.'
                : 'This content is protected. Enter your access code to reveal the story within.'}
            </p>
            <form onSubmit={handleCodeSubmit} className="w-full max-w-sm">
              <div className="relative">
                <Key className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-orange-600 dark:text-orange-400" />
                <input
                  type={blogData.accessType === 'PASSWORD' ? 'password' : 'text'}
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  placeholder={
                    blogData.accessType === 'PASSWORD' ? 'Enter password' : 'Enter your magic code'
                  }
                  className="w-full pl-12 pr-14 py-4 text-base border-2 border-orange-200 dark:border-orange-800/30 rounded-full bg-white/90 dark:bg-gray-800/90 transition-all focus:outline-none focus:border-orange-500 focus:bg-white dark:focus:bg-gray-800 focus:shadow-[0_0_0_4px_rgba(251,146,60,0.1)]"
                  disabled={isAuthLoading}
                  required
                />
                <button
                  type="submit"
                  disabled={!accessCode || isAuthLoading}
                  className="absolute right-1 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-full transition-all hover:scale-105 disabled:hover:scale-100"
                >
                  {isAuthLoading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <ArrowRight className="w-5 h-5" />
                  )}
                </button>
              </div>
              {authError && (
                <p className="text-red-500 text-sm mt-2 text-center animate-fadeIn">{authError}</p>
              )}
            </form>
          </motion.div>
        );

      case 'member-exclusive':
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center p-12 text-center"
          >
            <div className="w-24 h-24 bg-gradient-to-br from-purple-400 via-pink-500 to-yellow-500 rounded-full flex items-center justify-center mb-8 shadow-2xl animate-pulse">
              <Crown className="w-12 h-12 text-white" />
            </div>
            <h3 className="text-3xl font-bold mb-4 bg-gradient-to-r from-purple-600 via-pink-600 to-yellow-600 bg-clip-text text-transparent">
              Members-Only Masterpiece
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md text-lg">
              Join our exclusive community to access premium stories, insights, and experiences.
            </p>
            <button
              onClick={() => onAction?.('become_member', blogData)}
              className="px-8 py-4 bg-gradient-to-r from-purple-600 via-pink-600 to-yellow-600 text-white rounded-full hover:shadow-xl transition-all transform hover:-translate-y-1 flex items-center gap-3 text-lg font-medium cursor-pointer"
            >
              <Crown className="w-5 h-5" />
              Become a Member
            </button>
            <p className="text-sm text-gray-500 mt-4">Starting at $9.99/month • Cancel anytime</p>
          </motion.div>
        );

      default:
        return null;
    }
  };

  // Determine atmosphere preset based on content or page context
  const atmospherePreset = useMemo(() => {
    // First check if there's an atmospheric theme in the page
    const pageAtmosphere = document.querySelector('[class*="atmosphere-"]');
    if (pageAtmosphere) {
      const classes = pageAtmosphere.className.split(' ');
      const atmosphereClass = classes.find((c) => c.startsWith('atmosphere-'));
      if (atmosphereClass) {
        const preset = atmosphereClass.replace('atmosphere-', '');
        if (['regenerative', 'revolutionary', 'innovative', 'community'].includes(preset)) {
          return preset;
        }
      }
    }

    // Fallback to content theme detection
    if (blogData.theme?.name) {
      const themeName = blogData.theme.name.toLowerCase();
      if (themeName.includes('green') || themeName.includes('earth')) return 'regenerative';
      if (themeName.includes('red') || themeName.includes('fire')) return 'revolutionary';
      if (themeName.includes('blue') || themeName.includes('ocean')) return 'innovative';
      if (themeName.includes('orange') || themeName.includes('yellow') || themeName.includes('sun'))
        return 'community';
    }
    if (blogData.theme?.primaryColor) {
      const color = blogData.theme.primaryColor.toLowerCase();
      if (color.includes('green')) return 'regenerative';
      if (color.includes('red')) return 'revolutionary';
      if (color.includes('blue')) return 'innovative';
      if (color.includes('orange') || color.includes('yellow')) return 'community';
    }
    return 'innovative'; // default
  }, [blogData.theme]);

  // Use atmosphere preset as default theme if no theme is selected
  const effectiveTheme = currentReadingTheme || atmospherePreset;
  const theme = readingThemes[effectiveTheme] || readingThemes.regenerative;

  return (
    <div
      className={cn(
        'relative min-h-screen reading-themed',
        'atmosphere-enabled',
        `atmosphere-${atmospherePreset}`,
      )}
    >
      {/* Reading Atmosphere Layer */}
      <div className="reading-atmosphere" />

      {/* Progress Bar */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-gray-200/30 dark:bg-gray-800/30 z-50">
        <motion.div
          className="h-full bg-blue-600"
          style={{ width: `${scrollProgress}%` }}
          transition={{ duration: 0.1 }}
        />
      </div>

      {/* Close Button */}
      {/* {onClose && (
        <button
          onClick={onClose}
          className="fixed top-4 right-4 z-[60] p-3 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 cursor-pointer"
          aria-label="Close"
        >
          <X className="w-6 h-6" />
        </button>
      )} */}

      {/* Hero Section */}
      <div
        ref={heroRef}
        className="relative h-[70vh] md:h-[80vh] overflow-hidden reading-hero-themed atmosphere-section flex-shrink-0"
      >
        {blogData.displayImage ? (
          <motion.div
            className="absolute inset-0"
            style={{
              scale: heroScale,
              opacity: heroOpacity,
            }}
          >
            <img
              src={blogData.displayImage}
              alt={blogData.title}
              className="absolute inset-0 w-full h-full object-cover"
              loading="eager"
              onLoad={() => setHeroLoaded(true)}
            />
            {/* Advanced scrim gradient for optimal readability */}
            <div
              className="absolute inset-0 hero-scrim-mobile"
              style={{
                background: `linear-gradient(
                  to bottom,
                  rgba(0, 0, 0, 0.3) 0%,
                  rgba(0, 0, 0, 0.1) 30%,
                  rgba(0, 0, 0, 0.1) 60%,
                  rgba(0, 0, 0, 0.4) 75%,
                  rgba(0, 0, 0, 0.7) 100%
                )`,
              }}
            />
            {/* Additional vignette for edge darkening */}
            <div
              className="absolute inset-0"
              style={{
                background: `radial-gradient(
                  ellipse at center,
                  transparent 0%,
                  transparent 40%,
                  rgba(0, 0, 0, 0.4) 100%
                )`,
              }}
            />
            {/* Motion blur effect on scroll */}
            <motion.div
              className="absolute inset-0"
              style={{
                backdropFilter: `blur(${heroBlur}px)`,
              }}
            />
          </motion.div>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-primary-600 via-purple-600 to-pink-600">
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent" />
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-white rounded-full filter blur-3xl animate-pulse" />
              <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-purple-300 rounded-full filter blur-3xl animate-pulse delay-1000" />
            </div>
          </div>
        )}

        {/* Hero Content */}
        <div className="absolute inset-0 flex items-end">
          <div className="container mx-auto px-6 pb-12 md:pb-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: heroLoaded || !blogData.displayImage ? 1 : 0, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="max-w-4xl relative"
            >
              {/* Subtle backdrop blur for text area */}

              <div className="relative z-10 sm:px-6 px-2 py-6">
                {/* Category/Tags */}
                {blogData.elements && (
                  <div className="flex flex-wrap gap-2 mb-6">
                    <span className="px-4 py-1.5 bg-black/30 backdrop-blur-md text-white rounded-full text-sm font-medium border border-white/10">
                      Story
                    </span>
                    <span className="px-4 py-1.5 bg-black/30 backdrop-blur-md text-white rounded-full text-sm font-medium border border-white/10">
                      {readingTime} min read
                    </span>
                  </div>
                )}

                {/* Title */}
                <h1
                  className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-[1.1]"
                  style={{
                    textShadow:
                      '0 2px 4px rgba(0,0,0,0.8), 0 4px 8px rgba(0,0,0,0.6), 0 8px 16px rgba(0,0,0,0.4)',
                    letterSpacing: '-0.02em',
                    color: '#fff',
                  }}
                >
                  {blogData.title}
                </h1>

                {/* Description */}
                {blogData.description && (
                  <p
                    className="text-xl md:text-2xl text-white mb-8 leading-relaxed max-w-3xl"
                    style={{
                      textShadow:
                        '0 2px 4px rgba(0,0,0,0.8), 0 4px 8px rgba(0,0,0,0.6), 0 8px 16px rgba(0,0,0,0.4)',
                      letterSpacing: '0.01em',
                      color: '#fff',
                    }}
                  >
                    {blogData.description}
                  </p>
                )}

                {/* Author Info */}
                {blogData.vendor && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex items-center gap-4 flex-shrink-0">
                      <div className="relative w-14 h-14 flex-shrink-0">
                        {blogData.vendor.DisplayPic ? (
                          <Image
                            src={blogData.vendor.DisplayPic}
                            alt={blogData.vendor.Name}
                            className="w-14 h-14 rounded-full border-2 border-white/30 object-cover flex-shrink-0"
                            unoptimized
                            width={56}
                            height={56}
                          />
                        ) : (
                          <div className="w-14 h-14 bg-black/30 backdrop-blur-md rounded-full flex items-center justify-center border border-white/20 flex-shrink-0">
                            <User className="w-7 h-7 text-white" />
                          </div>
                        )}
                        <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-white shadow-lg" />
                      </div>
                      <div className="flex flex-col gap-1 text-white min-w-0">
                        <p
                          className="font-semibold text-lg truncate"
                          style={{ textShadow: '0 1px 3px rgba(0,0,0,0.8)', color: '#fff' }}
                        >
                          {blogData.vendor.Name}
                        </p>
                        {blogData.vendor.Headline && (
                          <span style={{ textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}>
                            {blogData.vendor.Headline}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-white/90 flex-shrink-0">
                      <span
                        className="flex items-center gap-1 whitespace-nowrap"
                        style={{ textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}
                      >
                        <Calendar className="w-4 h-4" />
                        {formatDate(blogData.createdAt)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="text-white/60"
          >
            <ChevronDown className="w-8 h-8" />
          </motion.div>
        </motion.div>
      </div>

      {/* Content Area */}
      <div ref={contentRef} className="relative">
        <div className="container mx-auto px-6 py-12 md:py-16 max-w-4xl">
          {/* Access Gate */}
          {!hasFullAccess ? (
            <div className="min-h-[60vh] flex items-center justify-center">{renderAccessGate()}</div>
          ) : (
            <>
              {/* Generated Summary */}
              {blogData.generatedSummary?.summary && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="mb-8 p-6 bg-gray-50 dark:bg-gray-800/50 rounded-2xl"
                >
                  <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">Summary</h2>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    {blogData.generatedSummary.summary}
                  </p>
                </motion.div>
              )}

              {/* Key Questions */}
              {blogData.questions && blogData.questions.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="mb-12 p-8 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-3xl"
                >
                  <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                    <Sparkles className="w-6 h-6 text-purple-600" />
                    What You&apos;ll Discover
                  </h2>
                  <ul className="space-y-3">
                    {blogData.questions.map((question, index) => (
                      <motion.li
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 + index * 0.1 }}
                        className="flex items-start gap-3"
                      >
                        <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{question}</span>
                      </motion.li>
                    ))}
                  </ul>
                </motion.div>
              )}

              {/* Main Content */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="blog-immersive-content atmosphere-section"
              >
                <div className="p-6 bg-gray-50 dark:bg-gray-800/50 rounded-2xl backdrop-blur-sm">
                  {contentData ? (
                    <ContentViewer
                      content={contentData}
                      className="blog-immersive-wrapper"
                      hideNativeScrollbar={true}
                      showProgressBar={false}
                      showNavigation={true}
                      useViewportScroll={true}
                    />
                  ) : (
                    <ContentViewerViewport
                      content={htmlContent}
                      className="blog-immersive-wrapper"
                      showNavigation={true}
                      showProgressBar={false}
                      hideNativeScrollbar={true}
                    />
                  )}
                </div>
              </motion.div>

              {/* End Actions */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-16 pt-8 border-t border-gray-200 dark:border-gray-800"
              >
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-6">
                    <button
                      onClick={() => setIsLiked(!isLiked)}
                      className={cn(
                        'flex items-center gap-2 px-6 py-3 rounded-full transition-all',
                        isLiked
                          ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                          : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700',
                      )}
                    >
                      <Heart className={cn('w-5 h-5', isLiked && 'fill-current')} />
                      <span className="font-medium">{isLiked ? 'Loved' : 'Love this'}</span>
                    </button>
                    <button
                      onClick={() => setIsSaved(!isSaved)}
                      className={cn(
                        'flex items-center gap-2 px-6 py-3 rounded-full transition-all',
                        isSaved
                          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                          : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700',
                      )}
                    >
                      <Bookmark className={cn('w-5 h-5', isSaved && 'fill-current')} />
                      <span className="font-medium">{isSaved ? 'Saved' : 'Save'}</span>
                    </button>
                    <button
                      onClick={() => {
                        const shareLink = `${window.location.origin}/post?id=${blogData.id}`;
                        if (navigator.share) {
                          navigator.share({
                            title: blogData.title,
                            text: blogData.description,
                            url: shareLink,
                          });
                        } else {
                          navigator.clipboard.writeText(shareLink);
                          onAction?.('share', blogData, { method: 'clipboard', url: shareLink });
                        }
                      }}
                      className="flex items-center gap-2 px-6 py-3 bg-gray-100 dark:bg-gray-800 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    >
                      <Share2 className="w-5 h-5" />
                      <span className="font-medium">Share</span>
                    </button>
                  </div>
                  {blogData.id && (
                    <a
                      href={`/p/${blogData.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-full transition-all hover:shadow-lg"
                    >
                      Visit Full Blog
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>

                {/* Author Card */}
                {blogData.vendor && (
                  <div className="bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-8">
                    <div className="flex items-center gap-6">
                      {blogData.vendor.DisplayPic ? (
                        <Image
                          src={blogData.vendor.DisplayPic}
                          alt={blogData.vendor.Name}
                          width={80}
                          height={80}
                          className="rounded-full"
                          unoptimized
                        />
                      ) : (
                        <div className="w-20 h-20 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                          <User className="w-10 h-10 text-gray-500" />
                        </div>
                      )}
                      <div className="flex-1">
                        <h3 className="text-xl font-bold mb-1">{blogData.vendor.Name}</h3>
                        {blogData.vendor.Headline && (
                          <p className="text-gray-600 dark:text-gray-400 mb-3">{blogData.vendor.Headline}</p>
                        )}
                        <button className="text-primary-600 hover:text-primary-700 font-medium">
                          View all stories →
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            </>
          )}
        </div>
      </div>

      {/* Floating Action Bar */}
      {/* <AnimatePresence>
        {showActions && hasFullAccess && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-40 md:z-40"
            style={{
              bottom: typeof window !== 'undefined' && window.innerWidth < 768 ? '120px' : '32px',
              zIndex: typeof window !== 'undefined' && window.innerWidth < 768 ? 45 : 40,
            }}
          >
            <div className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-md rounded-full shadow-2xl px-6 py-3 flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <Clock className="w-4 h-4" />
                <span>{readingTime} min</span>
              </div>
              <div className="w-px h-6 bg-gray-300 dark:bg-gray-700" />
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setIsLiked(!isLiked);
                    onAction?.('like', blogData, { liked: !isLiked });
                  }}
                  className={cn(
                    'p-2 rounded-full transition-all cursor-pointer',
                    isLiked
                      ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800',
                  )}
                >
                  <Heart className={cn('w-5 h-5', isLiked && 'fill-current')} />
                </button>
                <button
                  onClick={() => {
                    setIsSaved(!isSaved);
                    onAction?.('save', blogData, { saved: !isSaved });
                  }}
                  className={cn(
                    'p-2 rounded-full transition-all cursor-pointer',
                    isSaved
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800',
                  )}
                >
                  <Bookmark className={cn('w-5 h-5', isSaved && 'fill-current')} />
                </button>
                <button
                  onClick={() => {
                    const shareLink = `${window.location.origin}/post?id=${blogData.id}`;
                    if (navigator.share) {
                      navigator.share({
                        title: blogData.title,
                        text: blogData.description,
                        url: shareLink,
                      });
                    } else {
                      navigator.clipboard.writeText(shareLink);
                      onAction?.('share', blogData, { method: 'clipboard', url: shareLink });
                    }
                  }}
                  className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                >
                  <Share2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence> */}

      {/* Mini Progress Indicators */}
      {hasFullAccess && blogData.elements && blogData.elements.length > 3 && (
        <div className="fixed right-8 top-1/2 transform -translate-y-1/2 z-30 hidden lg:block">
          <div className="space-y-2">
            {blogData.elements.map((element, index) => {
              if (
                (element.contentType === 'custom' ||
                  element.contentType === 'text' ||
                  element.contentType === 'TEXT') &&
                element.content?.content?.text
              ) {
                const sectionId = `section-${index}`;
                const isActive = activeSection === sectionId;

                return (
                  <button
                    key={element._id}
                    onClick={() => {
                      sectionsRef.current[sectionId]?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className={cn(
                      'w-2 h-8 rounded-full transition-all',
                      isActive
                        ? 'bg-primary-600 w-3'
                        : 'bg-gray-300 dark:bg-gray-700 hover:bg-gray-400 dark:hover:bg-gray-600',
                    )}
                  />
                );
              }
              return null;
            })}
          </div>
        </div>
      )}

      {/* Lightbox */}
      <Lightbox open={lightboxOpen} onClose={() => setLightboxOpen(false)} currentIndex={0} total={1}>
        {lightboxImage && (
          <img
            src={lightboxImage.url}
            alt={lightboxImage.alt || 'Full size image'}
            className="max-w-full max-h-full object-contain"
          />
        )}
      </Lightbox>

      {/* Reading Theme Switcher */}
      <ReadingThemeSwitcher
        defaultTheme={effectiveTheme}
        onThemeChange={(themeId) => {
          setCurrentReadingTheme(themeId);
          // Find the scrollable container and smooth scroll to top
          const scrollContainer = document.querySelector('.overflow-y-auto') || window;
          if (scrollContainer === window) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else {
            (scrollContainer as HTMLElement).scrollTo({ top: 0, behavior: 'smooth' });
          }
        }}
      />
    </div>
  );
};

export default BlogInspectImmersive;
